import React from 'react';
import { z } from 'zod';
import { Flex, Text, Button } from '@chakra-ui/react';
import { Modal, TextArea, useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreateSubmissionNote } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: () => void;
  isLoading: boolean;
  name?: string;
  checklistCategory?: string;
  submissionId: string;
  submissionContextId: string;
}

const AddNoteModal: React.FC<Props> = props => {
  const {
    isOpen,
    onClose,
    onAdd,
    checklistCategory,
    isLoading,
    name = 'A Note',
    submissionId,
    submissionContextId
  } = props;
  const toast = useToast();
  const {
    fieldsResponse: { organizationId }
  } = useSubmissionData();

  const createNoteMutation = useCreateSubmissionNote({
    queryKey: ['submission-notes', { submissionId }],
    submissionId,
    submissionContextId,
    organizationId
  });

  const methods = useForm<{
    note: string;
  }>({
    defaultValues: {
      note: ''
    },
    resolver: zodResolver(
      z.object({
        note: z.string().trim().min(1, 'Please add a note.')
      })
    )
  });

  const {
    handleSubmit,
    register,
    watch,
    getValues,
    formState: { errors },
    setValue
  } = methods;

  const handleAddNote = async () => {
    const { note: content } = getValues();
    const response = await createNoteMutation.mutateAsync({
      title: 'Untitled',
      content,
      ...(checklistCategory
        ? {
            checklistCategory,
            category: 'Assessment'
          }
        : {
            category: 'General'
          })
    });
    if (response.isSuccess) {
      setValue('note', '');
      if (checklistCategory) {
        toast({
          variant: 'success',
          title: 'Assessment Note Added',
          description: 'The Note was stored in the Notes tab.'
        });
      } else {
        toast({
          variant: 'success',
          title: 'Note Added',
          description: 'The Note was stored in the Notes tab.'
        });
      }

      onAdd();
    } else {
      toast({
        variant: 'error',
        title: response.message
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="450px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleAddNote)}>
          <Flex flexDirection="column" gap={6} padding={8}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Add {name}</Text>
              <Text textStyle="textBody">
                This note will be logged in the Notes tab and will be available to all team members
              </Text>
            </Flex>

            <Flex gap={2} flexDirection="column">
              <Text textStyle="headingItem">{name}</Text>
              <TextArea
                value={watch('note')}
                {...register('note')}
                placeholder="Write a note for this assessment"
                isInvalid={Boolean(errors.note)}
                errorMessage={errors?.note?.message}
                rows={2}
              />
            </Flex>

            <Flex flexDirection="row" gap={2} justifyContent="end">
              <Button
                onClick={() => {
                  setValue('note', '');
                  onClose();
                }}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={isLoading}
                isLoading={isLoading}
                // onClick={()=> handleAddNote()}
              >
                Save Note
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default AddNoteModal;
